import type { RefObject } from 'react';

/**
 * Returns the nearest `<dialog>` (or `role="dialog"`) ancestor for use as a portal
 * `container` (e.g. on `TooltipContent`, `PopoverContent`). Falls back to `undefined`
 * (i.e. `document.body`) when no dialog ancestor is found.
 *
 * The simplest pattern is a ref on the trigger plus this hook to resolve the dialog
 * ancestor. Any ref inside the dialog works; the trigger is the natural pick.
 *
 * Skip the hook and pass a different ref directly as `container` only when you have a
 * reason to portal somewhere other than the dialog (e.g. a specific scroll container).
 *
 * @example
 * const triggerRef = useRef<HTMLButtonElement>(null);
 * const container = useDialogContainer(triggerRef);
 *
 * <Tooltip>
 *   <TooltipTrigger ref={triggerRef}>Hover me</TooltipTrigger>
 *   <TooltipContent container={container}>Label</TooltipContent>
 * </Tooltip>
 */
export const useDialogContainer = (ref: RefObject<Element | null> | null | undefined): HTMLElement | undefined => {
  return (ref?.current?.closest('dialog, [role="dialog"]') as HTMLElement | null) ?? undefined;
};
